import { defineFeature } from '../define-feature';
import { TEST_IDS, sidebarNavTestId } from '../test-ids';

export default defineFeature({
  id: 'agenda.appointment.update',
  category: 'agenda',
  title: 'Modifier ou déplacer un rendez-vous',
  intent:
    'Reprogrammer un rendez-vous existant ou en changer les détails (créneau, prestation, membre, client, note) sans le recréer.',
  context: {
    why: "Les plans changent tous les jours dans une activité de service — un client vous appelle pour décaler d'une heure, un imprévu vous impose de déplacer plusieurs rendez-vous, une prestation doit être rallongée parce qu'un soin complémentaire s'ajoute. Modifier ou déplacer un rendez-vous en quelques secondes plutôt que de tout resaisir manuellement représente un gain de temps considérable au quotidien, et surtout, ça garantit que votre agenda et vos clients restent synchronisés. Chaque modification déclenche automatiquement une notification au client, ce qui vous évite les allers-retours téléphoniques pour confirmer un changement d'horaire. Deux boutons distincts vous sont proposés selon ce que vous voulez faire : « Reprogrammer » pour changer uniquement la date et l'heure d'un rendez-vous existant, « Modifier » pour éditer tous ses détails (prestation, membre, client, lieu, montant, note, couleur). Cette distinction n'est pas cosmétique : elle permet de déclencher les bonnes notifications et de tracer proprement l'historique de vos rendez-vous. Enfin, pour les modifications les plus rapides, vous n'avez même pas besoin d'ouvrir la fiche du rendez-vous : glissez-déposez directement sur la grille de l'agenda pour changer le créneau, ou tirez le bord du rendez-vous pour rallonger ou raccourcir sa durée. C'est le geste le plus rapide possible pour un ajustement mineur.",
    use_cases: [
      "Un client vous demande de décaler son rendez-vous d'une heure : glissez-déposez la case sur le nouveau créneau, la modification et la notification sont automatiques.",
      'Vous ajoutez un soin complémentaire au rendez-vous : changez la prestation, et la durée, le prix et le lieu se recalculent automatiquement.',
      "Un praticien est finalement indisponible : réaffectez le rendez-vous à un autre membre de l'équipe.",
      'Vous corrigez une note ou le lieu du rendez-vous après un échange avec le client : passez par « Modifier » pour éditer les détails.',
      "Un rendez-vous déborde en durée réelle : rallongez-le en tirant le bord de la case sur la grille de l'agenda.",
      'Vous devez déplacer un rendez-vous récurrent : décidez si vous modifiez cette occurrence seulement ou toute la série.',
    ],
  },
  required_scopes: ['agenda.appointment.update'],
  prerequisites: [
    {
      ref: 'agenda.appointment.create',
      requirement: 'required',
      why: 'Un rendez-vous doit déjà exister avant de pouvoir le modifier ou le déplacer.',
    },
  ],
  ui_entry_points: [
    { route: '/agenda', element: 'Clic sur un rendez-vous → « Modifier »' },
    { route: '/agenda', element: "Glisser-déposer / redimensionnement d'un rendez-vous sur la grille" },
  ],
  api: {
    primary_operation_id: 'AppointmentAppController_putAppointment',
    related_operation_ids: [
      'AppointmentAppController_getAppointmentDetail',
      'AvailabilityAppController_getAvailabilityCalendar',
      'PrestationAppController_getProPrestation',
      'ProCustomerAppController_getProCustomer',
    ],
  },
  steps: [
    '**Ouvrez le rendez-vous** : cliquez sur le RDV pour afficher son détail.',
    "**Choisissez l'action** dans la cartouche « Actions » : **Reprogrammer** (replanifier le créneau — changer la date / l'heure) ou **Modifier** (éditer les détails via le formulaire : prestation, membre, client, lieu, montant, note, couleur).",
    '**Déplacez rapidement (alternative)** : glissez-déposez le RDV sur un autre créneau de la grille, ou redimensionnez-le pour changer sa durée.',
    "**Enregistrez** : l'agenda est mis à jour et le client reçoit la notification de modification.",
  ],
  gotchas: [
    '**Reprogrammer vs Modifier** : deux boutons distincts. « Reprogrammer » = replanifier le créneau (date/heure). « Modifier » = ouvrir le formulaire complet pour éditer les détails (prestation, membre, client, lieu, montant, note, couleur).',
    "**Glisser-déposer** : le moyen le plus rapide de déplacer un RDV sur un autre créneau — directement sur la grille de l'agenda.",
    '**Redimensionnement** : allongez ou raccourcissez la durée en tirant le bord du RDV sur la grille.',
    "**Changer la prestation** recalcule automatiquement durée, prix et lieu : vérifiez le montant si vous l'aviez ajusté à la main.",
    "**Réaffecter à un autre membre** : changez l'intervenant dans le formulaire. Attention aux disponibilités du nouveau membre pour éviter le double-booking.",
    "**RDV récurrent** : préciser si l'on modifie cette occurrence uniquement ou toute la série (comportement produit à confirmer).",
    '**Notification client** : toute modification déclenche une notification (selon la configuration de notification côté paramètres de réservation).',
    '**RDV passé ou bloqué** : peut ne pas être modifiable selon les règles produit (à confirmer).',
    '**Synchro Google Calendar** : si active, la modification est répercutée en temps réel côté Google.',
    "**Droits** : un membre sans le droit « toute l'équipe » ne peut modifier que ses propres RDV.",
  ],
  related_features: [
    'agenda.appointment.create',
    'agenda.appointment.status',
    'agenda.appointment.cancel',
    'catalog.service.manage',
  ],
  demo_script: {
    duration_target_s: 40,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption:
          'Un horaire à décaler, un soin à ajouter… Modifier un rendez-vous vous évite de tout resaisir, et votre client est prévenu.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/agenda'),
        caption: "Direction l'Agenda, dans le menu latéral.",
      },
      {
        action: 'wait_for',
        target: TEST_IDS.agenda_calendar,
        caption: 'Votre agenda s’affiche avec vos rendez-vous.',
      },
      {
        action: 'click',
        target: TEST_IDS.agenda_event_card,
        caption: 'On ouvre le rendez-vous à ajuster.',
        capture: true,
      },
      {
        action: 'click',
        target: TEST_IDS.appointment_edit,
        caption: '« Modifier » ouvre tous les détails ; « Reprogrammer » servirait juste à changer le créneau.',
      },
      {
        action: 'wait_for',
        target: TEST_IDS.appointment_name,
        caption: 'Le formulaire d’édition s’ouvre sur les détails du rendez-vous.',
      },
      {
        action: 'fill',
        target: TEST_IDS.appointment_name,
        value: 'Massage relaxant — Camille',
        caption: 'On ajuste ce qu’il faut, ici le titre du rendez-vous.',
        capture: true,
      },
      {
        action: 'click',
        target: TEST_IDS.appointment_submit,
        caption: 'On enregistre : l’agenda est à jour et le client reçoit la notification.',
      },
    ],
  },
});

// Verbatim Kaltouma (à conserver) :
// > Modifier ou déplacer un RDV existant : clic sur le RDV, ou glisser-déposer /
// > redimensionnement sur la grille. Sur la page détail, deux actions :
// > « Reprogrammer » (replanifier le créneau) et « Modifier » (éditer les détails).
